import React, { useState, useEffect, useMemo } from "react";
import {
  Home,
  MapPin,
  DollarSign,
  Bed,
  Bath,
  Square,
  Heart,
  LogOut,
} from "lucide-react";

/* =======================
   PROPERTY CARD
======================= */
const PropertyCard = ({
  property,
  favorites,
  toggleFavorite,
  view,
  currentUser,
  deleteProperty,
}) => {
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow">
      <div className="h-48 bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
        <Home className="w-20 h-20 text-white opacity-50" />
      </div>

      <div className="p-4">
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-xl font-bold text-gray-800">
            {property.title}
          </h3>

          <button
            onClick={() => toggleFavorite(property.id)}
            className="text-red-500 hover:scale-110 transition-transform"
          >
            <Heart
              className={`w-6 h-6 ${
                favorites.includes(property.id) ? "fill-current" : ""
              }`}
            />
          </button>
        </div>

        <div className="flex items-center text-gray-600 mb-2">
          <MapPin className="w-4 h-4 mr-1" />
          <span className="text-sm">{property.location}</span>
        </div>

        <div className="flex items-center text-green-600 font-bold text-xl mb-3">
          <DollarSign className="w-5 h-5" />
          <span>{property.price.toLocaleString()} FCFA / mois</span>
        </div>

        <div className="flex gap-4 mb-3 text-sm text-gray-600">
          <div className="flex items-center">
            <Bed className="w-4 h-4 mr-1" />
            {property.bedrooms} ch.
          </div>

          <div className="flex items-center">
            <Bath className="w-4 h-4 mr-1" />
            {property.bathrooms} sdb
          </div>

          {property.area && (
            <div className="flex items-center">
              <Square className="w-4 h-4 mr-1" />
              {property.area} m²
            </div>
          )}
        </div>

        <p className="text-gray-600 text-sm mb-3">
          {property.description}
        </p>

        <div className="flex gap-2">
          <a
            href={`https://wa.me/${
              process.env.REACT_APP_WHATSAPP || "2290163466596"
            }`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors text-center"
          >
            📱 WhatsApp LocaBénin
          </a>

          {view === "my-listings" &&
            currentUser &&
            property.ownerId === currentUser.id && (
              <button
                onClick={() => deleteProperty(property.id)}
                className="px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Supprimer
              </button>
            )}
        </div>
      </div>
    </div>
  );
};

/* =======================
   APP PRINCIPALE
======================= */
export default function RentalApp() {
  const [currentUser, setCurrentUser] = useState(null);
  const [view] = useState("explore");
  const [properties, setProperties] = useState([]);
  const [favorites, setFavorites] = useState([]);

  /* =======================
     LOCALSTORAGE
  ======================= */
  useEffect(() => {
    const u = localStorage.getItem("locabenin_currentUser");
    const p = localStorage.getItem("locabenin_properties");
    const f = localStorage.getItem("locabenin_favorites");

    if (u) setCurrentUser(JSON.parse(u));
    if (p) setProperties(JSON.parse(p));
    if (f) setFavorites(JSON.parse(f));
  }, []);

  useEffect(() => {
    localStorage.setItem(
      "locabenin_currentUser",
      JSON.stringify(currentUser)
    );
    localStorage.setItem(
      "locabenin_properties",
      JSON.stringify(properties)
    );
    localStorage.setItem(
      "locabenin_favorites",
      JSON.stringify(favorites)
    );
  }, [currentUser, properties, favorites]);

  /* =======================
     ANNONCES PAR DÉFAUT
  ======================= */
  useEffect(() => {
    if (properties.length === 0) {
      setProperties([
        {
          id: 1,
          title: "Chambre moderne à Cotonou",
          location: "Akpakpa, Cotonou",
          price: 25000,
          bedrooms: 1,
          bathrooms: 1,
          area: 20,
          description: "Chambre propre, sécurisée et bien située.",
          ownerId: 1,
        },
        {
          id: 2,
          title: "Studio meublé",
          location: "Fidjrossè, Cotonou",
          price: 60000,
          bedrooms: 1,
          bathrooms: 1,
          area: 35,
          description: "Studio climatisé avec cuisine équipée.",
          ownerId: 1,
        },
        {
          id: 3,
          title: "Appartement 2 chambres",
          location: "Calavi",
          price: 90000,
          bedrooms: 2,
          bathrooms: 2,
          area: 80,
          description: "Appartement spacieux dans un quartier calme.",
          ownerId: 1,
        },
      ]);
    }
  }, [properties]);

  /* =======================
     ACTIONS
  ======================= */
  const toggleFavorite = (id) => {
    setFavorites((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const deleteProperty = (id) => {
    const prop = properties.find((p) => p.id === id);
    if (!currentUser || prop.ownerId !== currentUser.id) return;

    if (window.confirm("Supprimer cette annonce ?")) {
      setProperties((prev) => prev.filter((p) => p.id !== id));
    }
  };

  /* =======================
     FILTRAGE
  ======================= */
  const filteredProperties = useMemo(() => {
    return properties;
  }, [properties]);

  /* =======================
     LOGIN DEMO
  ======================= */
  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600">
        <button
          onClick={() =>
            setCurrentUser({ id: 1, name: "Utilisateur Démo" })
          }
          className="bg-white px-6 py-3 rounded-lg font-bold"
        >
          Connexion démo
        </button>
      </div>
    );
  }

  /* =======================
     UI
  ======================= */
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow p-4 flex justify-between items-center">
        <h1 className="text-2xl font-bold">LocaBénin</h1>

        <button
          onClick={() => setCurrentUser(null)}
          className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg"
        >
          <LogOut size={18} />
        </button>
      </header>

      <main className="max-w-7xl mx-auto p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
        {filteredProperties.length === 0 && (
          <p className="col-span-full text-center text-gray-500">
            Aucune annonce disponible.
          </p>
        )}

        {filteredProperties.map((property) => (
          <PropertyCard
            key={property.id}
            property={property}
            favorites={favorites}
            toggleFavorite={toggleFavorite}
            view={view}
            currentUser={currentUser}
            deleteProperty={deleteProperty}
          />
        ))}
      </main>
    </div>
  );
}
